import { PhaseAction } from "../../dtos/PhaseAction";
import { PhaseDto } from "../../dtos/PhaseDto";
import { PhaseSubmissionDto } from "../../dtos/PhaseSubmissionDto";
import { StepDto } from "../../dtos/StepDto";
import { ApiResult, apiWrapperWithError } from "./ApiUtils";
import { withCalendarDates } from "./calendarDates";
import { announceGrantModulesMayHaveChanged } from "./moduleEvents";


export const getSteps = async (phaseSubmissionId: string): Promise<ApiResult<StepDto[]> | null> => {
    return apiWrapperWithError<StepDto[]>(`api/phases/submission/${phaseSubmissionId}/steps`, 'GET', `fetching steps for phase submission with ID ${phaseSubmissionId}`);
}

export const getPhaseById = async (phaseId: string): Promise<ApiResult<PhaseDto> | null> => {
    return apiWrapperWithError<PhaseDto>(`api/phases/${phaseId}`, 'GET', `fetching phase with ID ${phaseId}`);
}

export const getPhaseStepsById = async (phaseId: string): Promise<ApiResult<StepDto[]> | null> => {
    return apiWrapperWithError<StepDto[]>(`api/phases/${phaseId}/steps`, 'GET', `fetching phase steps with ID ${phaseId}`);
}

export const getPhasesByGrant = async (grantId: string, moduleId?: number): Promise<ApiResult<PhaseDto[]> | null> => {
    const url = moduleId ? `api/phases/byGrant/${grantId}?moduleId=${moduleId}` : `api/phases/byGrant/${grantId}`;
    return apiWrapperWithError<PhaseDto[]>(url, 'GET', `fetching phases for grant ${grantId}`);
}

export const setPhaseActionById = async (phaseId: string, phaseAction: PhaseAction, phase?: PhaseDto): Promise<ApiResult<PhaseDto> | null> => {
    const result = await apiWrapperWithError<PhaseDto>(`api/phases/${phaseId}/${phaseAction}`, 'PUT', `performing phase action ${phaseAction} on phase with ID ${phaseId}`, phase ? withCalendarDates(phase) : phase);

    // Approving the task is the other thing that fires trigger actions, so it can create a module
    // just as approving a subtask can.
    if (phaseAction === PhaseAction.APPROVE && !result?.error) {
        announceGrantModulesMayHaveChanged();
    }

    return result;
}

export const createResubmission = async (phaseId: string): Promise<ApiResult<PhaseSubmissionDto> | null> => {
    return apiWrapperWithError<PhaseSubmissionDto>(`api/phases/${phaseId}/resubmissions`, 'POST', `creating resubmission for phase with ID ${phaseId}`);
}

// Manual phase date override (extended header). A null value blanks that date (param omitted),
// which clears its overridden flag server-side so derivation can repopulate it.
export const updatePhaseDates = async (phaseId: string, submission: Date | null, completion: Date | null): Promise<ApiResult<PhaseDto> | null> => {
    const params = new URLSearchParams();
    if (submission) params.append("submission", new Date(submission).toISOString());
    if (completion) params.append("completion", new Date(completion).toISOString());
    const qs = params.toString();
    return apiWrapperWithError<PhaseDto>(`api/phases/${phaseId}/dates${qs ? "?" + qs : ""}`, 'PUT', `updating dates for phase with ID ${phaseId}`);
}

// Clears a manual override and restores the field's derived value (the ↺ restore icon).
export const restorePhaseDate = async (phaseId: string, field: "submission" | "completion"): Promise<ApiResult<PhaseDto> | null> => {
    const value = field === "submission" ? "Submission" : "Completion";
    return apiWrapperWithError<PhaseDto>(`api/phases/${phaseId}/dates/restore?field=${value}`, 'PUT', `restoring ${field} date for phase with ID ${phaseId}`);
}